import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useSubAgentAuth } from './Clients/SubAgent/SubAgentAuthContext';
import Loading from '../components/loading';
import Toast from '../components/Toast';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebaseConfig';

// Firebase
import { functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';

// redux
import { updateScreen } from '../redux/screenSlice';
import { useDispatch } from 'react-redux';
import { AppDispatch } from "../redux/store";


const Login: React.FC = () => {
  // Landing page state
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSubAgentLoginModal, setShowSubAgentLoginModal] = useState(false);

  // Consultant login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);



  // SubAgent login form state
  const [subAgentUsername, setSubAgentUsername] = useState('');
  const [subAgentPassword, setSubAgentPassword] = useState('');
  const [subAgentError, setSubAgentError] = useState('');
  const [subAgentPasswordVisible, setSubAgentPasswordVisible] = useState(false);
  const [subAgentUsernameError, setSubAgentUsernameError] = useState(false);
  const [subAgentPasswordError, setSubAgentPasswordError] = useState(false);

  // Forgot password state
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetEmailError, setResetEmailError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isToastError, setIsToastError] = useState(false);

  const { login, currentUser, isAdmin, isManager, isStaff } = useAuth();
  const { subAgentLogin, currentSubAgent } = useSubAgentAuth();

  const navigate = useNavigate();

  const dispatch = useDispatch<AppDispatch>();

  // No longer need the 3D card hover effects
  // Using simpler CSS hover animations for portal cards

  // Initialize particles background
  useEffect(() => {
    // Add particles.js library dynamically
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/particles.js/2.0.0/particles.min.js';
    script.async = true;
    script.onload = () => {
      // @ts-ignore
      if (window.particlesJS) {
        // @ts-ignore
        window.particlesJS('particles-js', {
          particles: {
            number: { value: 80, density: { enable: true, value_area: 800 } },
            color: { value: '#ffffff' },
            shape: { type: 'circle' },
            opacity: { value: 0.5, random: false },
            size: { value: 3, random: true },
            line_linked: {
              enable: true,
              distance: 150,
              color: '#ffffff',
              opacity: 0.4,
              width: 1
            },
            move: {
              enable: true,
              speed: 2,
              direction: 'none',
              random: false,
              straight: false,
              out_mode: 'out',
              bounce: false
            }
          },
          interactivity: {
            detect_on: 'canvas',
            events: {
              onhover: { enable: true, mode: 'grab' },
              onclick: { enable: true, mode: 'push' },
              resize: true
            },
            modes: {
              grab: { distance: 140, line_linked: { opacity: 1 } },
              push: { particles_nb: 4 },
            }
          },
          retina_detect: true
        });
      }
    };
    document.body.appendChild(script);

    // Add required CSS
    const style = document.createElement('style');
    style.innerHTML = `
      body {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      }
      .perspective-1000 {
        perspective: 1000px;
      }
      .feature-card-large {
        cursor: pointer;
        min-height: 400px;
      }
      .card-button {
        box-shadow: 0 10px 20px rgba(0,0,0,0.1);
      }
      @keyframes fade-in-down {
        0% {
          opacity: 0;
          transform: translateY(-20px);
        }
        100% {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .animate-fade-in-down {
        animation: fade-in-down 0.6s ease forwards;
      }
      #particles-js {
        position: fixed;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        z-index: 0;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.body.removeChild(script);
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    // Create a variable to track the current screen value
    let currentScreen = 'Staff';
    
    if (isAdmin) {
      currentScreen = 'Admin';
    } else if (isManager) {
      currentScreen = 'Manager';
    } else if (isStaff) {
      currentScreen = 'Staff';
    }
    
    // Only dispatch if we have a valid screen to update to
    if (currentScreen) {
      dispatch(updateScreen(currentScreen));
    }
  }, [isAdmin, isManager, isStaff, dispatch]);



  // Redirect subagent after login
  useEffect(() => {
    if (currentSubAgent) {
      console.log(currentSubAgent);
      navigate('/subagent/dashboard');
    }
  }, [currentSubAgent, navigate]);

  // useEffect(() => {
  //   if (currentUser) {
  //     navigate('/dashboard');
  //   }
  // }, [currentUser, navigate]);



  const handleSubAgentLoginNavigation = () => {
    // Show the subagent login modal instead of navigating
    setShowSubAgentLoginModal(true);
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };



  const toggleSubAgentPasswordVisibility = () => {
    setSubAgentPasswordVisible(!subAgentPasswordVisible);
  };

  const handleLogin = async () => {
    setError('');
    setEmailError(false);
    setPasswordError(false);

    if (!email) {
      setEmailError(true);
    }
    if (!password) {
      setPasswordError(true);
    }
    if (!email || !password) {
      setError('Please fill in all fields.');
      return;
    }

    setIsLoading(true);

    try {
      await login(email, password);
      // Note: screen state is updated in the useEffect hook based on isAdmin and isManager
    } catch (err: any) {
      console.error('Login error:', err);
      let errorMessage = 'Login failed. Please check your credentials.';

      // Handle specific error messages from Firebase Auth
      if (err?.code) {
        switch (err.code) {
          case 'auth/user-not-found':
            errorMessage = 'No account found with this email address.';
            break;
          case 'auth/wrong-password':
            errorMessage = 'Incorrect password. Please try again.';
            break;
          case 'auth/invalid-credential':
            errorMessage = 'Invalid email or password. Please check your credentials and try again.';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Invalid email format.';
            break;
          case 'auth/user-disabled':
            errorMessage = 'This account has been disabled. Please contact support.';
            break;
          case 'auth/too-many-requests':
            errorMessage = 'Too many failed login attempts. Please try again later.';
            break;
          default:
            errorMessage = 'Login failed. Please check your credentials and try again.';
        }
      }

      setError(errorMessage);
      setIsLoading(false);
    }
  };
  
  const handleForgotPasswordToggle = async () => {
    console.log('Forgot Password');

    // Define the callable function

    try {
      const sendPasswordReset = httpsCallable(functions, 'passwordReset');
      // Call the function with the provided email
      const result = await sendPasswordReset({ email: resetEmail }) as { data: { success: boolean, message: string } };

      if (result.data.success) {
        await sendPasswordResetEmail(auth, resetEmail);

        // Show success toast
        setToastMessage(result.data.message);
        setIsToastError(false);
        setShowToast(true);


        // refresh the page
        window.location.reload();
      }


    } catch (error: any) {
      console.error('Password reset error:', error);

      // Check if the error is due to a specific reason
      if (error.message.includes('function-call')) {
        console.error('Ensure the Cloud Function exists and is callable.');
      }
      // Notify the user of the error
    }
  };

  const handleForgotPassword = async () => {
    // Reset previous errors
    setResetEmailError('');

    // Validate email
    if (!resetEmail) {
      setResetEmailError('Email is required.');
      return;
    }

    setIsLoading(true);

    try {
      // Send password reset email
      await sendPasswordResetEmail(auth, resetEmail);

      // Show success toast
      setToastMessage('Password reset email sent successfully!');
      setIsToastError(false);
      setShowToast(true);

      // Navigate back to login after a short delay
      setTimeout(() => {
        setIsForgotPassword(false);
        setShowToast(false);
      }, 3000);
    } catch (error) {
      console.error('Password reset error:', error);

      // Show error toast
      setToastMessage(error instanceof Error ? error.message : 'Failed to send reset email.');
      setIsToastError(true);
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      isForgotPassword ? handleForgotPassword() : handleLogin();
    }
  };



  const handleSubAgentKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSubAgentLogin();
    }
  };



  const handleSubAgentLogin = async () => {
    setSubAgentError('');
    setSubAgentUsernameError(false);
    setSubAgentPasswordError(false);

    if (!subAgentUsername) {
      setSubAgentUsernameError(true);
    }
    if (!subAgentPassword) {
      setSubAgentPasswordError(true);
    }
    if (!subAgentUsername || !subAgentPassword) {
      setSubAgentError('Please fill in all fields.');
      return;
    }

    setIsLoading(true);

    try {
      await subAgentLogin(subAgentUsername, subAgentPassword);
      dispatch(updateScreen('SubAgent'));
    } catch (err: any) {
      console.error('SubAgent login error:', err);
      let errorMessage = 'Login failed. Please check your credentials.';

      // Handle specific error messages from the Cloud Function
      if (err?.message) {
        switch (err.message) {
          case 'INVALID_USERNAME_FORMAT':
            errorMessage = 'Invalid username format. Please use format: ID@company';
            break;
          case 'COMPANY_NOT_FOUND':
            errorMessage = 'Company not found. Please check your username.';
            break;
          case 'INVALID_CREDENTIALS':
            errorMessage = 'Invalid username or password.';
            break;
          default:
            errorMessage = err.message;
        }
      }

      setSubAgentError(errorMessage);
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Main content section with background image */}
      <div
        className="md:block w-full flex flex-col justify-between p-4 bg-cover bg-center relative overflow-y-auto"
        style={{
          backgroundImage: `url('${process.env.PUBLIC_URL}/images/desktop/bg-login.png')`,
        }}
      >
        {/* Logo */}
        <div className="absolute top-7 left-12 z-10">
          <img
            src={`${process.env.PUBLIC_URL}/images/desktop/login-main-logo1.svg`}
            alt="ScholarCRM Logo"
            className="h-16"
          />
        </div>

        {/* Login navigation buttons for mobile view */}
        <div className="md:hidden flex justify-end mt-4 mb-6 mr-4">
          <button
            onClick={() => setShowLoginModal(true)}
            className="bg-indigo-600 text-white py-2 px-4 rounded-lg shadow-md"
          >
            Consultant Login
          </button>
        </div>

        {/* Animated background particles */}
        <div className="particles-container absolute inset-0 z-0 overflow-hidden" id="particles-js"></div>

        {/* Main content area */}
        <div className="relative z-10 mt-10 flex flex-col items-center">
          <div className="w-full max-w-6xl mx-auto px-4">
            {/* Floating login portal section with glass morphism */}
            <div className="mb-20 mt-4">
              <div className="text-center mb-10 animate-fade-in-down">
                <h2 className="text-4xl font-extrabold text-center mb-3 text-white drop-shadow-lg">
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-purple-500 to-blue-500">
                    Welcome to ScholarCRM
                  </span>
                </h2>
                <p className="text-lg text-black/90 max-w-2xl mx-auto">
                  <span className="backdrop-blur-sm bg-white/10 px-3 py-1 rounded-lg">Choose your portal to get started</span>
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-4xl mx-auto perspective-1000">
                {/* Consultant Login Card */}
                <div className="feature-card-large transform transition-all duration-300 hover:scale-105 hover:translate-y-[-5px]">
                  <div className="bg-gradient-to-br from-indigo-50 to-white rounded-2xl overflow-hidden shadow-xl border border-indigo-100 h-full flex flex-col backdrop-blur-lg">
                    <div className="relative overflow-hidden h-32 bg-gradient-to-r from-indigo-500 to-indigo-700">
                      <div className="absolute top-0 left-0 w-full h-full">
                        <div className="w-32 h-32 bg-indigo-400 rounded-full absolute -top-10 -right-10 opacity-20"></div>
                        <div className="w-24 h-24 bg-indigo-300 rounded-full absolute top-20 -left-10 opacity-20"></div>
                      </div>
                      <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2">
                        <div className="rounded-full p-3 bg-white shadow-lg">
                          <div className="rounded-full bg-indigo-100 w-16 h-16 flex items-center justify-center text-4xl">
                            👨‍💼
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-8 pt-12 flex flex-col flex-grow">
                      <h3 className="text-2xl font-bold mb-2 text-indigo-800 text-center">
                        <span className="relative inline-block">
                          Consultant Login
                          <span className="absolute bottom-0 left-0 w-full h-1 bg-indigo-600 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></span>
                        </span>
                      </h3>
                      <p className="text-gray-600 text-center mb-8 font-light">
                        For administrators, managers and consultants managing leads and applications
                      </p>
                      <div className="mt-auto">
                        <button
                          onClick={() => setShowLoginModal(true)}
                          className="card-button w-full bg-gradient-to-r from-indigo-600 to-indigo-700 text-white py-4 rounded-xl hover:shadow-lg transition-all flex items-center justify-center font-semibold focus:ring-4 focus:ring-indigo-200 focus:ring-opacity-50 group relative overflow-hidden"
                        >
                          <span className="absolute inset-0 w-full h-full transition duration-300 ease-out transform -translate-x-full bg-gradient-to-r from-indigo-700 to-indigo-800 group-hover:translate-x-0"></span>
                          <span className="relative flex items-center">
                            <span className="mr-3">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                              </svg>
                            </span>
                            Access as Consultant
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>



                {/* Affiliate Partner Login Card */}
                <div className="feature-card-large transform transition-all duration-300 hover:scale-105 hover:translate-y-[-5px]" style={{ transitionDelay: '0.05s' }}>
                  <div className="bg-gradient-to-br from-purple-50 to-white rounded-2xl overflow-hidden shadow-xl border border-purple-100 h-full flex flex-col backdrop-blur-lg">
                    <div className="relative overflow-hidden h-32 bg-gradient-to-r from-purple-500 to-purple-700">
                      <div className="absolute top-0 left-0 w-full h-full">
                        <div className="w-32 h-32 bg-purple-400 rounded-full absolute -top-10 -right-10 opacity-20"></div>
                        <div className="w-24 h-24 bg-purple-300 rounded-full absolute top-20 -left-10 opacity-20"></div>
                      </div>
                      <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2">
                        <div className="rounded-full p-3 bg-white shadow-lg">
                          <div className="rounded-full bg-purple-100 w-16 h-16 flex items-center justify-center text-4xl">
                            🏢
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-8 pt-12 flex flex-col flex-grow">
                      <h3 className="text-2xl font-bold mb-2 text-purple-800 text-center">
                        <span className="relative inline-block">
                          Affiliate Partner Login
                          <span className="absolute bottom-0 left-0 w-full h-1 bg-purple-600 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></span>
                        </span>
                      </h3>
                      <p className="text-gray-600 text-center mb-8 font-light">
                        For partners and affiliate partners to manage referrals and track commissions
                      </p>
                      <div className="mt-auto">
                        <button
                          onClick={handleSubAgentLoginNavigation}
                          className="card-button w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white py-4 rounded-xl hover:shadow-lg transition-all flex items-center justify-center font-semibold focus:ring-4 focus:ring-purple-200 focus:ring-opacity-50 group relative overflow-hidden"
                        >
                          <span className="absolute inset-0 w-full h-full transition duration-300 ease-out transform -translate-x-full bg-gradient-to-r from-purple-700 to-purple-800 group-hover:translate-x-0"></span>
                          <span className="relative flex items-center">
                            <span className="mr-3">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                              </svg>
                            </span>
                            Access as Affiliate Partner
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero section */}
            <div className="text-center mb-12 animate-fade-in-down" style={{ animationDelay: '0.2s' }}>
              <h1 className="text-4xl md:text-6xl font-extrabold text-center mb-5 text-white drop-shadow-lg">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-purple-500 to-blue-500">
                  Your Complete Study Abroad Solution
                </span>
              </h1>
            </div>

            {/* Features section */}
            <div className="mb-16 mt-8">

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Feature 1 */}
                <div className="feature-card transform transition-all duration-300 hover:scale-105 hover:translate-y-[-5px]">
                  <div className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-indigo-50 h-full relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-b from-indigo-200 to-transparent rounded-bl-full opacity-30 -mt-4 -mr-4"></div>
                    <div className="relative">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center mb-6 shadow-lg transform -rotate-3">
                        <span className="text-white text-2xl">
                          <img src={`${process.env.PUBLIC_URL}/images/desktop/facebook_landingpage.svg`} alt="Facebook" className="w-8 h-8 text-white" />
                        </span>
                      </div>
                      <h3 className="text-xl font-bold mb-3 text-indigo-900">Seamless Meta Integration</h3>
                      <div className="h-1 w-12 bg-indigo-500 mb-4 rounded-full"></div>
                      <p className="text-slate-700 font-light">
                        Easily connect your Meta accounts to ScholarCRM to automatically capture leads and manage applications directly from your social media presence.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Feature 1 */}
                <div className="feature-card transform transition-all duration-300 hover:scale-105 hover:translate-y-[-5px]">
                  <div className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-indigo-50 h-full relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-b from-indigo-200 to-transparent rounded-bl-full opacity-30 -mt-4 -mr-4"></div>
                    <div className="relative">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center mb-6 shadow-lg transform -rotate-3">
                        <span className="text-white text-2xl">
                          <img src={`${process.env.PUBLIC_URL}/images/desktop/currency.svg`} alt="Facebook" className="w-8 h-8 text-white" />
                        </span>
                      </div>
                      <h3 className="text-xl font-bold mb-3 text-indigo-900">Currency Remittance</h3>
                      <div className="h-1 w-12 bg-indigo-500 mb-4 rounded-full"></div>
                      <p className="text-slate-700 font-light">
                        Fast and secure international payments with competitive exchange rates for tuition and living expenses.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Feature 2 */}
                <div className="feature-card transform transition-all duration-300 hover:scale-105 hover:translate-y-[-5px]" style={{ transitionDelay: '0.05s' }}>
                  <div className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-blue-50 h-full relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-b from-blue-200 to-transparent rounded-bl-full opacity-30 -mt-4 -mr-4"></div>
                    <div className="relative">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center mb-6 shadow-lg transform -rotate-3">
                      <span className="text-white text-2xl">
                          <img src={`${process.env.PUBLIC_URL}/images/desktop/loans.svg`} alt="Facebook" className="w-8 h-8 text-white" />
                        </span>
                      </div>
                      <h3 className="text-xl font-bold mb-3 text-blue-900">Student Loans</h3>
                      <div className="h-1 w-12 bg-blue-500 mb-4 rounded-full"></div>
                      <p className="text-slate-700 font-light">
                        Streamlined loan processing service with favorable terms tailored for international students.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Feature 3 */}
                <div className="feature-card transform transition-all duration-300 hover:scale-105 hover:translate-y-[-5px]" style={{ transitionDelay: '0.1s' }}>
                  <div className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-purple-50 h-full relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-b from-purple-200 to-transparent rounded-bl-full opacity-30 -mt-4 -mr-4"></div>
                    <div className="relative">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center mb-6 shadow-lg transform -rotate-3">
                      <span className="text-white text-2xl">
                          <img src={`${process.env.PUBLIC_URL}/images/desktop/house.svg`} alt="Facebook" className="w-8 h-8 text-white" />
                        </span>
                      </div>
                      <h3 className="text-xl font-bold mb-3 text-purple-900">Student Accommodation</h3>
                      <div className="h-1 w-12 bg-purple-500 mb-4 rounded-full"></div>
                      <p className="text-slate-700 font-light">
                        Secure quality housing options before arrival with verified listings and virtual tours.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Feature 4 */}
                <div className="feature-card transform transition-all duration-300 hover:scale-105 hover:translate-y-[-5px]" style={{ transitionDelay: '0.15s' }}>
                  <div className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-indigo-50 h-full relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-b from-indigo-200 to-transparent rounded-bl-full opacity-30 -mt-4 -mr-4"></div>
                    <div className="relative">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center mb-6 shadow-lg transform -rotate-3">
                      <span className="text-white text-2xl">
                          <img src={`${process.env.PUBLIC_URL}/images/desktop/airplane.svg`} alt="Facebook" className="w-8 h-8 text-white" />
                        </span>
                      </div>
                      <h3 className="text-xl font-bold mb-3 text-indigo-900">Travel Insurance</h3>
                      <div className="h-1 w-12 bg-indigo-500 mb-4 rounded-full"></div>
                      <p className="text-slate-700 font-light">
                        Comprehensive travel and health insurance packages tailored for international students.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Feature 5 */}
                <div className="feature-card transform transition-all duration-300 hover:scale-105 hover:translate-y-[-5px]" style={{ transitionDelay: '0.2s' }}>
                  <div className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-blue-50 h-full relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-b from-blue-200 to-transparent rounded-bl-full opacity-30 -mt-4 -mr-4"></div>
                    <div className="relative">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center mb-6 shadow-lg transform -rotate-3">
                      <span className="text-white text-2xl">
                          <img src={`${process.env.PUBLIC_URL}/images/desktop/student.svg`} alt="Facebook" className="w-11 h-11 text-white" />
                        </span>
                      </div>
                      <h3 className="text-xl font-bold mb-3 text-blue-900">Student Login</h3>
                      <div className="h-1 w-12 bg-blue-500 mb-4 rounded-full"></div>
                      <p className="text-slate-700 font-light">
                        Track applications, upload documents, and communicate with consultants in real-time.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Feature 6 */}
                <div className="feature-card transform transition-all duration-300 hover:scale-105 hover:translate-y-[-5px]" style={{ transitionDelay: '0.25s' }}>
                  <div className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-purple-50 h-full relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-b from-purple-200 to-transparent rounded-bl-full opacity-30 -mt-4 -mr-4"></div>
                    <div className="relative">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center mb-6 shadow-lg transform -rotate-3">
                      <span className="text-white text-2xl">
                          <img src={`${process.env.PUBLIC_URL}/images/desktop/businessman.svg`} alt="Facebook" className="w-8 h-8 text-white" />
                        </span>
                      </div>
                      <h3 className="text-xl font-bold mb-3 text-purple-900">Affiliate Partner Management</h3>
                      <div className="h-1 w-12 bg-purple-500 mb-4 rounded-full"></div>
                      <p className="text-slate-700 font-light">
                        Efficient partner management with lead tracking and commission calculations.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer section */}
            <div className="mb-12 animate-fade-in-down" style={{ animationDelay: '0.2s' }}>
              <h1 className="text-3xl md:text-3xl font-extrabold mb-5 text-white drop-shadow-lg">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-purple-500 to-blue-500">
                  Important Documents
                </span>
              </h1>
            </div>

            <div className="mb-16 mt-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Document 1 */}
                <Link to="/terms-and-conditions" className="no-underline">
                  <div className="feature-card transform transition-all duration-300 hover:scale-105 hover:translate-y-[-5px]">
                    <div className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-indigo-50 h-full relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-b from-indigo-200 to-transparent rounded-bl-full opacity-30 -mt-4 -mr-4"></div>
                      <div className="relative">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center mb-6 shadow-lg transform -rotate-3">
                          <span className="text-white text-2xl">
                            <img src={`${process.env.PUBLIC_URL}/images/desktop/terms-and-condition.svg`} alt="Terms and Conditions" className="w-8 h-8 text-white" />
                          </span>
                        </div>
                        <h3 className="text-xl font-bold mb-3 text-indigo-900">Terms & Conditions</h3>
                        <div className="h-1 w-12 bg-indigo-500 mb-4 rounded-full"></div>
                        <p className="text-slate-700 font-light">
                          Review our terms and conditions to understand the terms and conditions for using our platform.
                        </p>
                      </div>
                    </div>
                  </div>
                </Link>

                {/* Document 2 */}
                <Link to="/privacy-policy" className="no-underline">
                  <div className="feature-card transform transition-all duration-300 hover:scale-105 hover:translate-y-[-5px]">
                    <div className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-indigo-50 h-full relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-b from-indigo-200 to-transparent rounded-bl-full opacity-30 -mt-4 -mr-4"></div>
                      <div className="relative">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center mb-6 shadow-lg transform -rotate-3">
                          <span className="text-white text-2xl">
                            <img src={`${process.env.PUBLIC_URL}/images/desktop/privacy-policy.svg`} alt="Privacy Policy" className="w-8 h-8 text-white" />
                          </span>
                        </div>
                        <h3 className="text-xl font-bold mb-3 text-indigo-900">Privacy Policy</h3>
                        <div className="h-1 w-12 bg-indigo-500 mb-4 rounded-full"></div>
                        <p className="text-slate-700 font-light">
                          Review our privacy policy to understand how we protect your personal information.
                        </p>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Login Modal */}
      {showLoginModal && (
        <>
          {/* Animation styles */}
          <style>
            {`
              @keyframes fadeIn {
                from { opacity: 0; transform: scale(0.95); }
                to { opacity: 1; transform: scale(1); }
              }
              .animate-fadeIn {
                animation: fadeIn 0.3s ease-out forwards;
              }
            `}
          </style>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden animate-fadeIn">
              {/* Modal Header */}
              <div className="flex justify-between items-center p-4 border-b">
                <div className="flex items-center">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/desktop/login-main-logo.svg`}
                    alt="ScholarCRM Logo"
                    className="h-8 mr-2"
                  />
                  <h2 className="text-xl font-semibold text-gray-800">
                    {!isForgotPassword ? 'Consultant Login' : 'Reset Password'}
                  </h2>
                </div>
                <button
                  onClick={() => {
                    setShowLoginModal(false);
                    setIsForgotPassword(false);
                    setError('');
                    setEmailError(false);
                    setPasswordError(false);
                    setResetEmailError('');
                  }}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  <svg className="h-6 w-6" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                    <path d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                {!isForgotPassword ? (
                  // Login Form
                  <>
                    <p className="text-sm text-gray-600 mb-6">
                      Enter your credentials to access the consultant dashboard
                    </p>

                    <div className="mb-4">
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="text"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Enter your email"
                        className={`w-full p-2.5 text-sm border ${emailError ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 ${emailError ? 'focus:ring-red-500' : 'focus:ring-indigo-500'}`}
                      />
                      {emailError && <p className="text-red-500 text-xs mt-1">Email is required.</p>}
                    </div>

                    <div className="mb-4 relative">
                      <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                        Password
                      </label>
                      <input
                        type={passwordVisible ? 'text' : 'password'}
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Enter your password"
                        className={`w-full p-2.5 text-sm border ${passwordError ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 ${passwordError ? 'focus:ring-red-500' : 'focus:ring-indigo-500'} pr-10`}
                      />
                      <div
                        className="absolute top-[63%] transform -translate-y-1/2 right-3 cursor-pointer"
                        onClick={togglePasswordVisibility}
                      >
                        <img
                          src={passwordVisible ? `${process.env.PUBLIC_URL}/images/desktop/eye-open.svg` : `${process.env.PUBLIC_URL}/images/desktop/eye-close.svg`}
                          alt="Toggle Password Visibility"
                          className="w-5 h-5"
                        />
                      </div>
                      {passwordError && <p className="text-red-500 text-xs mt-1">Password is required.</p>}
                    </div>

                    <div className="flex justify-end mb-6">
                      <button
                        type="button"
                        className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                        onClick={() => setIsForgotPassword(true)}
                      >
                        Forgot Password?
                      </button>
                    </div>

                    <button
                      onClick={handleLogin}
                      className="w-full py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition duration-200 text-center"
                    >
                      Sign In
                    </button>

                    {error && (
                      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-red-600 text-sm">{error}</p>
                      </div>
                    )}
                  </>
                ) : (
                  // Forgot Password Form
                  <>
                    <p className="text-sm text-gray-600 mb-6">
                      Enter your email address and we'll send you a link to reset your password.
                    </p>

                    <div className="mb-6">
                      <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="reset-email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Enter your email"
                        className={`w-full p-2.5 text-sm border ${resetEmailError ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 ${resetEmailError ? 'focus:ring-red-500' : 'focus:ring-indigo-500'}`}
                      />
                      {resetEmailError && <p className="text-red-500 text-xs mt-1">{resetEmailError}</p>}
                    </div>

                    <div className="flex flex-col space-y-3">
                      <button
                        onClick={handleForgotPasswordToggle}
                        className="w-full py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition duration-200 text-center"
                      >
                        Send Reset Link
                      </button>

                      <button
                        onClick={() => setIsForgotPassword(false)}
                        className="w-full py-2.5 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition duration-200 text-center"
                      >
                        Back to Login
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}



      {/* SubAgent Login Modal */}
      {showSubAgentLoginModal && (
        <>
          {/* Animation styles - reused from consultant login */}
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden animate-fadeIn">
              {/* Modal Header */}
              <div className="flex justify-between items-center p-4 border-b">
                <div className="flex items-center">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/desktop/login-main-logo.svg`}
                    alt="ScholarCRM Logo"
                    className="h-8 mr-2"
                  />
                  <h2 className="text-xl font-semibold text-gray-800">Affiliate Partner Login</h2>
                </div>
                <button
                  onClick={() => {
                    setShowSubAgentLoginModal(false);
                    setSubAgentError('');
                    setSubAgentUsernameError(false);
                    setSubAgentPasswordError(false);
                  }}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  <svg className="h-6 w-6" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                    <path d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                <p className="text-sm text-gray-600 mb-6">
                  Enter your sub-agent credentials to access your dashboard
                </p>

                <div className="mb-4">
                  <label htmlFor="subagent-username" className="block text-sm font-medium text-gray-700 mb-1">
                    Username
                  </label>
                  <input
                    type="text"
                    id="subagent-username"
                    value={subAgentUsername}
                    onChange={(e) => setSubAgentUsername(e.target.value)}
                    onKeyPress={handleSubAgentKeyPress}
                    placeholder="Enter your username"
                    className={`w-full p-2.5 text-sm border ${subAgentUsernameError ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 ${subAgentUsernameError ? 'focus:ring-red-500' : 'focus:ring-indigo-500'}`}
                  />
                  {subAgentUsernameError && <p className="text-red-500 text-xs mt-1">Username is required.</p>}
                </div>

                <div className="mb-6 relative">
                  <label htmlFor="subagent-password" className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <input
                    type={subAgentPasswordVisible ? 'text' : 'password'}
                    id="subagent-password"
                    value={subAgentPassword}
                    onChange={(e) => setSubAgentPassword(e.target.value)}
                    onKeyPress={handleSubAgentKeyPress}
                    placeholder="Enter your password"
                    className={`w-full p-2.5 text-sm border ${subAgentPasswordError ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 ${subAgentPasswordError ? 'focus:ring-red-500' : 'focus:ring-indigo-500'} pr-10`}
                  />
                  <div
                    className="absolute top-[63%] transform -translate-y-1/2 right-3 cursor-pointer"
                    onClick={toggleSubAgentPasswordVisibility}
                  >
                    <img
                      src={subAgentPasswordVisible ? `${process.env.PUBLIC_URL}/images/desktop/eye-open.svg` : `${process.env.PUBLIC_URL}/images/desktop/eye-close.svg`}
                      alt="Toggle Password Visibility"
                      className="w-5 h-5"
                    />
                  </div>
                  {subAgentPasswordError && <p className="text-red-500 text-xs mt-1">Password is required.</p>}
                </div>

                <button
                  onClick={handleSubAgentLogin}
                  className="w-full py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition duration-200 text-center"
                >
                  Sign In
                </button>

                {subAgentError && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-600 text-sm">{subAgentError}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Toast Component */}
      <Toast
        message={toastMessage}
        isError={isToastError}
        show={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  );
};

export default Login;